<template>
  <div>
    <v-container bg fill-height grid-list-md text-xs-center>
      <v-row no-gutters align-center class="pt-5">
        <v-col cols="6" sm="12" offset-sm3>
          <v-card class="justify-center">
            <v-card-text class="text-center pt-4">
              <h4 class="font-weight-light">
                You still have no results for this mission
              </h4>
              <p v-if="!workInProgress" class="font-weight-light subheading">
                Please perform the computation to show the results
              </p>
              <p
                v-if="workInProgress && computing"
                class="font-weight-light subheading"
              >
                Computing ...
              </p>
              <p
                v-if="workInProgress && !computing"
                class="font-weight-light subheading"
              >
                Loading results ...
              </p>
            </v-card-text>

            <v-divider class="p-0 m-0"></v-divider>

            <v-card-actions class="justify-center p-4">
              <progress-dialog
                :callbackStatus="callbackStatus"
                :computing.sync="computing"
                :workInProgress.sync="workInProgress"
                :text="'Start Computation'"
                :button-color="'#5757FF'"
                :visibility="visibility"
              ></progress-dialog>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import ProgressDialog from './ProgressDialog'
import { mapGetters } from 'vuex'
import Plotly from 'plotly.js-dist'
import dataTools from '../../tools/data.js'
import dashboardTools from '../../tools/dashboard.js'
import Vue from 'vue'

export default {
  name: 'exo-mission-computation',
  components: { ProgressDialog },
  data() {
    return {
      computing: false,
      workInProgress: false,
      plotLiveArea: 'livePlotMap',
      visibility: true
    }
  },

  mounted() {
    this.visibility = true
    this.callbackStatus()
  },

  beforeDestroy() {
    this.visibility = false
    this.computing = false
    try {
      Plotly.purge(this.plotLiveArea)
    } catch (error) {
      // https://www.youtube.com/watch?v=dQw4w9WgXcQ
    }
  },

  computed: {
    ...mapGetters({
      currentUser: 'currentUser',
      currentMission: 'currentMission'
    }),

    livePlotActive() {
      return (
        this.computing && this.currentMission.typeName === 'SimulationRequest'
      )
    }
  },

  watch: {
    computing() {
      if (this.livePlotActive) {
        Vue.nextTick(() => {
          this.drawPlotLiveMap()
        })
      } else {
        try {
          Plotly.purge(this.plotLiveArea)
        } catch (error) {}
      }
    }
  },

  methods: {
    addLiveTraces(data) {
      if (data == null || data.length === 0 || data[0] == null) {
        return
      }
      const nbDaysDisplayed = 20
      const oldTime = (data[0] - 86400 * nbDaysDisplayed) / 86400
      const newTime = data[0] / 86400
      const semiMajorAxis =
        Math.round(dataTools.convertSi2gui('distance', data[1]) * 100) / 100
      const eccentricity =
        Math.round(dataTools.convertSi2gui('coefficient', data[2]) * 100000) /
        100000
      const inclination =
        Math.round(dataTools.convertSi2gui('angle', data[3]) * 1000) / 1000
      let minuteView = {
        xaxis: {
          title: {
            text: 'Days',
            standoff: 30
          },
          range: [oldTime < 0 ? 0 : oldTime, newTime],
          domain: [0.1, 0.95]
        }
      }
      Plotly.relayout(this.plotLiveArea, minuteView)
      Plotly.extendTraces(
        this.plotLiveArea,
        {
          y: [[semiMajorAxis], [eccentricity], [inclination]],
          x: [[newTime], [newTime], [newTime]]
        },
        [0, 1, 2]
      )
    },

    drawPlotLiveMap() {
      let semiMajorAxisTrace = {
        name: 'Semi-major axis (km)',
        y: [],
        x: [0],
        mode: 'lines',
        line: {
          color: 'green'
        }
      }
      let eccentricityTrace = {
        name: 'Eccentricity',
        yaxis: 'y2',
        y: [],
        x: [0],
        mode: 'lines',
        line: {
          color: 'blue'
        }
      }
      let inclinationTrace = {
        name: 'Inclination (deg)',
        yaxis: 'y3',
        y: [],
        x: [0],
        mode: 'lines',
        line: {
          color: 'red'
        }
      }
      let data = [semiMajorAxisTrace, eccentricityTrace, inclinationTrace]
      let layout = {
        legend: { orientation: 'h' },
        // autosize: false,
        width: '900',
        height: '300',
        xaxis: {
          domain: [0.2, 0.85]
        },
        yaxis: {
          title:
            'Semi-major axis (' + dataTools.getDefaultGuiUnit('distance') + ')',
          side: 'left',
          position: 0,
          titlefont: { color: 'green' },
          tickfont: { color: 'green' }
        },
        yaxis2: {
          title: 'Eccentricity',
          side: 'left',
          position: 0.1,
          overlaying: 'y',
          titlefont: { color: 'blue' },
          tickfont: { color: 'blue' }
        },
        yaxis3: {
          title: 'Inclination (' + dataTools.getDefaultGuiUnit('angle') + ')',
          side: 'right',
          overlaying: 'y',
          titlefont: { color: 'red' },
          tickfont: { color: 'red' }
        }
      }
      Plotly.newPlot(this.plotLiveArea, data, layout)
    },

    callbackStatus() {
      if (this.livePlotActive) {
        this.$http
          .get(
            dashboardTools.getCurrentAPI(this) +
              '/status_details/' +
              this.currentMission.id
          )
          .then((response) => {
            this.addLiveTraces(response.data)
          })
          .catch(() => {})
      }
    }
  }
}
</script>

<style scoped>
@keyframes greenWhiteFade {
  0% {
    background-color: #c8e6c9;
  }
  50% {
    background-color: #a5d6a7;
  }
  100% {
    background-color: white;
  }
}

@-webkit-keyframes greenWhiteFade {
  0% {
    background-color: #c8e6c9;
  }
  50% {
    background-color: #a5d6a7;
  }
  100% {
    background-color: white;
  }
}

.blinkdiv {
  background-color: white;
  animation-name: greenWhiteFade;
  -webkit-animation-name: greenWhiteFade;
  animation-iteration-count: 1;
  -webkit-animation-iteration-count: 1;
  animation-duration: 2s;
  -webkit-animation-duration: 2s;
}
</style>
